import React, { useMemo, useRef, useState } from 'react';
import { useOrg } from '../../hooks/useOrg';
import { useLocations } from '../../hooks/useLocations';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { api } from '../../lib/api';
import { pricePerLocation } from '../../lib/pipStates';
import { parseCsvRows, toCsv } from '../../lib/csv';
import { AlertTriangle, Check, FileUp, Download, X, CircleAlert, SkipForward } from 'lucide-react';

// Bulk-import locations from a spreadsheet. Four steps: pick a file, review
// what we parsed out of it, confirm the billing change, then import.
//
// The review step is the point of the whole thing — every row is billed, so
// nobody should be able to upload a file and find out afterwards what it
// cost. Validation mirrors server/src/routes/locations.js (POST /bulk); the
// server re-checks everything and is the authority.

const COLUMNS = ['name', 'address_line1', 'address_line2', 'city', 'state', 'zip', 'phone', 'timezone'];

// Header spellings we accept for each field. Everything is snake_cased and
// lowercased by parseCsvRows before it gets here, so this only has to cover
// genuinely different words ("street" for "address_line1"), not casing.
const ALIASES = {
  name:          ['name', 'location_name', 'location', 'office', 'office_name'],
  address_line1: ['address_line1', 'address_line_1', 'address1', 'address_1', 'address', 'street', 'street_address'],
  address_line2: ['address_line2', 'address_line_2', 'address2', 'address_2', 'suite', 'unit'],
  city:          ['city', 'town'],
  state:         ['state', 'st'],
  zip:           ['zip', 'zipcode', 'zip_code', 'postal_code', 'postal'],
  phone:         ['phone', 'phone_number', 'telephone', 'tel'],
  timezone:      ['timezone', 'time_zone', 'tz'],
};

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
];

const TEMPLATE_ROWS = [
  { name: 'Tampa Office',   address_line1: '101 E Kennedy Blvd', address_line2: 'Suite 300', city: 'Tampa',   state: 'FL', zip: '33602', phone: '(813) 555-0142', timezone: 'America/New_York' },
  { name: 'Brandon Clinic', address_line1: '505 Providence Rd',  address_line2: '',          city: 'Brandon', state: 'FL', zip: '33511', phone: '(813) 555-0177', timezone: 'America/New_York' },
];

function fmtMoney(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });
}

// Pull a canonical field out of a parsed row, trying each accepted spelling.
function pick(row, field) {
  for (const key of ALIASES[field]) {
    if (row[key] != null && String(row[key]).trim() !== '') return String(row[key]).trim();
  }
  return '';
}

function validate(row) {
  const errors = [];
  if (!row.name) errors.push('Name is required');
  if (row.state && !/^[A-Za-z]{2}$/.test(row.state)) errors.push(`State "${row.state}" must be a 2-letter code`);
  if (row.zip && !/^\d{5}(-\d{4})?$/.test(row.zip)) errors.push(`ZIP "${row.zip}" must be 5 digits`);
  if (row.timezone && !TIMEZONES.includes(row.timezone)) errors.push(`Timezone "${row.timezone}" is not supported`);
  return errors;
}

export default function LocationImportModal({ onClose, onImported }) {
  const { currentOrg } = useOrg();
  const { locations, refetchLocations } = useLocations();
  const { subscription, refresh: refreshSubscription } = useSubscription();

  const [step, setStep] = useState('upload');   // upload → review → confirm → done
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState([]);      // [{ line, data, errors, duplicate }]
  const [unknownCols, setUnknownCols] = useState([]);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const existingNames = useMemo(
    () => new Set(locations.map(l => String(l.name).trim().toLowerCase())),
    [locations]
  );

  const handleFile = (file) => {
    if (!file) return;
    setError('');
    setFileName(file.name);

    const reader = new FileReader();
    reader.onerror = () => setError('Could not read that file.');
    reader.onload = () => {
      try {
        const { headers, rows } = parseCsvRows(reader.result);
        if (!rows.length) {
          setError('That file has a header row but no data rows.');
          return;
        }

        const known = new Set(Object.values(ALIASES).flat());
        setUnknownCols(headers.filter(h => h && !known.has(h)));

        // Names repeated inside the file itself count as duplicates too —
        // only the first occurrence will be created.
        const seen = new Set(existingNames);
        const out = rows.map(r => {
          const data = {
            name:          pick(r, 'name'),
            address_line1: pick(r, 'address_line1'),
            address_line2: pick(r, 'address_line2'),
            city:          pick(r, 'city'),
            state:         pick(r, 'state').toUpperCase(),
            zip:           pick(r, 'zip'),
            phone:         pick(r, 'phone'),
            timezone:      pick(r, 'timezone') || 'America/New_York',
          };
          const errors = validate(data);
          const key = data.name.toLowerCase();
          const duplicate = !!key && seen.has(key);
          if (key) seen.add(key);
          return { line: r._line, data, errors, duplicate };
        });

        setParsed(out);
        setStep('review');
      } catch (err) {
        setError(`Could not parse that file: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([toCsv(COLUMNS, TEMPLATE_ROWS)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'medilink-locations-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const badRows = parsed.filter(r => r.errors.length);
  const dupRows = parsed.filter(r => !r.errors.length && r.duplicate);
  const goodRows = parsed.filter(r => !r.errors.length && !r.duplicate);

  const unitAmount = subscription?.unit_amount ?? pricePerLocation(currentOrg?.state, currentOrg?.provider_type) * 100;
  const currentQty = subscription?.quantity ?? locations.length ?? 1;
  // Law firms are free — no per-location billing, so no pricing step.
  const billable = currentOrg?.type === 'medical_practice';

  const doImport = async () => {
    setError('');
    setImporting(true);
    try {
      const payload = goodRows.map(r => ({ ...r.data, _line: r.line }));
      const res = await api.bulkCreateLocations(currentOrg.id, payload);
      await refetchLocations();
      refreshSubscription?.();
      setResult(res);
      setStep('done');
      if (onImported) onImported(res);
    } catch (err) {
      setError(err.message);
      setStep('review');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setParsed([]);
    setFileName('');
    setUnknownCols([]);
    setError('');
    setStep('upload');
    if (fileRef.current) fileRef.current.value = '';
  };

  const titles = {
    upload:  'Import locations from CSV',
    review:  'Review import',
    confirm: 'Confirm billing change',
    done:    'Import complete',
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-overlay backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-elevated border border-theme rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme">
          <h3 className="text-[16px] font-bold text-primary-c">{titles[step]}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-[8px] flex items-center justify-center text-muted-c hover:bg-hover-surface transition-all">
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">{error}</div>
          )}

          {step === 'upload' && (
            <>
              <p className="text-[13px] text-muted-c leading-relaxed">
                Upload a CSV with one row per location. Only <span className="text-primary-c font-semibold">name</span> is
                required — address, phone, and timezone are optional. Nothing is created until you review and confirm.
              </p>

              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                onClick={() => fileRef.current?.click()}
                className={`rounded-[14px] border-2 border-dashed p-10 text-center cursor-pointer transition-all ${
                  dragging ? 'border-[#0DA7CA] bg-accent-glow' : 'border-strong hover:border-accent-light/50 bg-secondary'
                }`}
              >
                <div className="w-12 h-12 rounded-[12px] bg-accent-glow flex items-center justify-center text-accent-light mx-auto mb-3">
                  <FileUp size={20} strokeWidth={2.2} />
                </div>
                <div className="text-[13px] font-semibold text-primary-c">Drop a CSV here, or click to choose</div>
                <div className="text-[12px] text-muted-c mt-1">Up to 200 locations per import</div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={e => handleFile(e.target.files?.[0])}
                />
              </div>

              <div className="rounded-[12px] border border-theme bg-secondary p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-primary-c mb-1">Expected columns</div>
                    <div className="text-[11px] text-muted-c font-mono break-words">{COLUMNS.join(', ')}</div>
                  </div>
                  <button
                    onClick={downloadTemplate}
                    className="flex-shrink-0 px-3 py-2 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all flex items-center gap-1.5"
                  >
                    <Download size={13} strokeWidth={2.2} />
                    Template
                  </button>
                </div>
              </div>
            </>
          )}

          {step === 'review' && (
            <>
              <div className="flex items-center gap-2 flex-wrap text-[12px]">
                <span className="text-muted-c">{fileName}</span>
                <span className="px-2 py-1 rounded-[8px] bg-accent-glow text-accent-light font-semibold">{goodRows.length} to import</span>
                {dupRows.length > 0 && (
                  <span className="px-2 py-1 rounded-[8px] bg-warning-soft text-warning font-semibold">{dupRows.length} skipped</span>
                )}
                {badRows.length > 0 && (
                  <span className="px-2 py-1 rounded-[8px] bg-danger-soft text-danger font-semibold">{badRows.length} with errors</span>
                )}
              </div>

              {unknownCols.length > 0 && (
                <div className="px-4 py-3 rounded-[10px] bg-warning-soft border border-strong text-[12px] text-primary-c">
                  Ignored {unknownCols.length === 1 ? 'column' : 'columns'}:{' '}
                  <span className="font-mono">{unknownCols.join(', ')}</span>
                </div>
              )}

              {badRows.length > 0 && (
                <div className="px-4 py-3 rounded-[10px] bg-danger-soft border border-strong text-[12px] text-primary-c">
                  Fix the rows below in your spreadsheet and upload again — nothing is imported while there are errors.
                </div>
              )}

              <div className="rounded-[12px] border border-theme overflow-hidden">
                <div className="max-h-[320px] overflow-y-auto">
                  <table className="w-full text-[12px]">
                    <thead className="sticky top-0 bg-secondary">
                      <tr className="text-left text-muted-c">
                        <th className="px-3 py-2 font-semibold w-12">Row</th>
                        <th className="px-3 py-2 font-semibold">Name</th>
                        <th className="px-3 py-2 font-semibold">Address</th>
                        <th className="px-3 py-2 font-semibold w-40">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.map(r => (
                        <tr key={r.line} className="border-t border-theme">
                          <td className="px-3 py-2 text-muted-c">{r.line}</td>
                          <td className="px-3 py-2 text-primary-c font-medium truncate max-w-[160px]">{r.data.name || <span className="text-muted-c">—</span>}</td>
                          <td className="px-3 py-2 text-muted-c truncate max-w-[200px]">
                            {[r.data.address_line1, r.data.city, r.data.state, r.data.zip].filter(Boolean).join(', ') || '—'}
                          </td>
                          <td className="px-3 py-2">
                            {r.errors.length ? (
                              <span className="text-danger flex items-start gap-1.5">
                                <CircleAlert size={13} className="flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                                <span>{r.errors.join('; ')}</span>
                              </span>
                            ) : r.duplicate ? (
                              <span className="text-warning flex items-center gap-1.5">
                                <SkipForward size={13} strokeWidth={2.2} /> Already exists
                              </span>
                            ) : (
                              <span className="text-success flex items-center gap-1.5">
                                <Check size={13} strokeWidth={2.5} /> Ready
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-theme">
                <button
                  type="button"
                  onClick={reset}
                  className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                >
                  Choose another file
                </button>
                <button
                  type="button"
                  onClick={() => (billable ? setStep('confirm') : doImport())}
                  disabled={!goodRows.length || badRows.length > 0 || importing}
                  className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0"
                >
                  {billable
                    ? `Continue — ${goodRows.length} ${goodRows.length === 1 ? 'location' : 'locations'}`
                    : importing ? 'Importing…' : `Import ${goodRows.length} ${goodRows.length === 1 ? 'location' : 'locations'}`}
                </button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              <div className="rounded-[12px] border border-strong bg-warning-soft p-4 flex items-start gap-3">
                <AlertTriangle size={18} className="text-warning flex-shrink-0 mt-0.5" />
                <div className="text-[13px] text-primary-c">
                  <div className="font-semibold mb-1">
                    Importing {goodRows.length} {goodRows.length === 1 ? 'location' : 'locations'} — free until a referred patient books there
                  </div>
                  <div className="text-muted-c text-[12px]">
                    Nothing is added to your bill now. Each imported location joins your subscription at {fmtMoney(unitAmount)}/month only 24 hours after a referred patient's first visit there is confirmed. You can switch any of them to paid from Billing.
                  </div>
                </div>
              </div>

              <div className="rounded-[12px] border border-theme overflow-hidden">
                <div className="p-4 space-y-2 text-[12px]">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-c">Current locations</span>
                    <span className="text-primary-c">{currentQty} × {fmtMoney(unitAmount)} = <span className="font-semibold">{fmtMoney(unitAmount * currentQty)}</span></span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-c">Importing (free until a referral)</span>
                    <span className="text-primary-c">+{goodRows.length} × $0 = <span className="font-semibold text-accent-light">+$0</span></span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-theme">
                    <span className="text-secondary-c font-semibold">Monthly total today</span>
                    <span className="text-primary-c font-bold">{fmtMoney(unitAmount * currentQty)}/mo (unchanged)</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-theme">
                <button
                  type="button"
                  onClick={() => setStep('review')}
                  disabled={importing}
                  className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={doImport}
                  disabled={importing}
                  className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0"
                >
                  {importing ? 'Importing…' : `Import ${goodRows.length} ${goodRows.length === 1 ? 'location' : 'locations'}`}
                </button>
              </div>
            </>
          )}

          {step === 'done' && (
            <>
              <div className="rounded-[12px] border border-theme bg-secondary p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-success-soft text-success flex items-center justify-center mx-auto mb-3">
                  <Check size={22} strokeWidth={2.5} />
                </div>
                <div className="text-[14px] font-semibold text-primary-c">
                  {result?.count || 0} {result?.count === 1 ? 'location' : 'locations'} imported
                </div>
                {!!result?.skipped?.length && (
                  <div className="text-[12px] text-muted-c mt-1">
                    {result.skipped.length} skipped as duplicates
                  </div>
                )}
                <div className="text-[12px] text-muted-c mt-2">
                  Map pins fill in over the next few minutes as addresses are geocoded.
                </div>
              </div>

              <div className="flex justify-end pt-3 border-t border-theme">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all"
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
